// Phase B.4 — Admin Shofar Manager: upload + library + broadcast trigger.
//
// Each clip ≤ 5MB, ≤ 20s, MP3/MP4/WAV/M4A. Admin clicks "Sound now" to fire
// a server broadcast — every listener polling /api/shofar/active-broadcast
// within the TTL window queues the clip at their next clean break (handled
// in PlayerContext).

import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
  Megaphone, Upload, Trash2, ToggleLeft, ToggleRight, AlertCircle, Check, Volume2, Sparkles,
} from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024;
const MAX_DURATION = 20.0;
const ACCEPT = "audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,video/mp4";

function formatDuration(s) {
  if (!Number.isFinite(s) || s <= 0) return "—";
  return `${s.toFixed(1)}s`;
}

function readDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = file.type.startsWith("video/")
      ? document.createElement("video")
      : document.createElement("audio");
    el.preload = "metadata";
    el.muted = true;
    el.onloadedmetadata = () => {
      const d = el.duration;
      URL.revokeObjectURL(url);
      if (!Number.isFinite(d)) reject(new Error("Could not read duration"));
      else resolve(d);
    };
    el.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read media")); };
    el.src = url;
  });
}

export default function ShofarManager() {
  const [clips, setClips] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [broadcastFlash, setBroadcastFlash] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/shofar");
      setClips(data || []);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) { setErr("File too large (max 5MB)"); return; }
    setErr("");
    setFile(f);
    if (!label) setLabel(f.name.replace(/\.[^.]+$/, ""));
  };

  const upload = async () => {
    setErr("");
    if (!label.trim()) { setErr("Label is required"); return; }
    if (!file) { setErr("Pick an audio file first"); return; }
    setUploading(true);
    try {
      const dur = await readDuration(file);
      if (dur > MAX_DURATION + 0.5) {
        setErr(`Clip must be ${MAX_DURATION}s or less (got ${dur.toFixed(1)}s)`);
        setUploading(false);
        return;
      }
      const fd = new FormData();
      fd.append("label", label.trim());
      fd.append("active", "true");
      fd.append("duration_seconds", String(dur));
      fd.append("audio", file);
      await api.post("/admin/shofar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLabel(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setUploading(false); }
  };

  const toggleActive = async (c) => {
    try { await api.patch(`/admin/shofar/${c.id}`, { active: !c.active }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete shofar "${c.label}"?`)) return;
    try { await api.delete(`/admin/shofar/${c.id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const broadcast = async (c) => {
    setErr("");
    try {
      const { data } = await api.post(`/admin/shofar/${c.id}/broadcast`);
      setBroadcastFlash({ id: c.id, ttl: data.expires_in_seconds });
      setTimeout(() => setBroadcastFlash(null), 5000);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <section className="card-surface p-5 md:p-6 space-y-5" data-testid="shofar-manager">
      <header>
        <div className="overline text-[#C5A059] text-[10px]">Phase B.4</div>
        <h2 className="font-serif text-[#FDFBF7] text-xl md:text-2xl tracking-tight flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
          Shofar Library
        </h2>
        <p className="text-[#A1A4AB] text-xs mt-0.5">Upload short shofar clips. Click <em>Sound now</em> to broadcast — every active listener will hear the shofar at their next clean track break (within {MAX_DURATION}s).</p>
      </header>

      {err && (
        <div className="text-red-300 text-xs flex items-center gap-2" data-testid="shofar-err">
          <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> {err}
        </div>
      )}

      {/* Upload */}
      <div className="rounded-lg border border-[#22262E] bg-[#0E1014]/60 p-4 space-y-3" data-testid="shofar-upload-form">
        <div className="overline text-[10px] text-[#A1A4AB]">Upload new clip</div>
        <input
          placeholder="Label (e.g. Tekiah · Sabbath open)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="input-lux text-sm w-full"
          data-testid="shofar-upload-label"
        />
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          onChange={onPick}
          className="block w-full text-xs text-[#A1A4AB] file:overline file:text-[10px] file:px-3 file:py-1.5 file:rounded-md file:border file:border-[#22262E] file:bg-transparent file:text-[#FDFBF7] file:hover:border-[#C5A059]/40 file:mr-3"
          data-testid="shofar-upload-file"
        />
        <div className="text-[10px] text-[#6C707A]">MP3 / MP4 / WAV / M4A · ≤ 5 MB · ≤ {MAX_DURATION}s</div>
        <div className="flex justify-end">
          <button
            onClick={upload}
            disabled={uploading || !file}
            data-testid="shofar-upload-save"
            className="overline text-xs px-3 py-1.5 rounded-md bg-[#C5A059] text-[#060709] hover:bg-[#DCC286] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Upload className="w-3.5 h-3.5" strokeWidth={2} /> {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="shofar-list">
        {loading ? (
          <div className="text-[#6C707A] text-sm">Loading clips…</div>
        ) : clips.length === 0 ? (
          <div className="text-[#6C707A] text-sm">No shofar clips yet.</div>
        ) : clips.map((c) => (
          <div key={c.id} data-testid={`shofar-row-${c.id}`} className="rounded-lg border border-[#22262E] bg-[#0a0a14]/40 p-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Volume2 className="w-3.5 h-3.5 text-[#C5A059] flex-shrink-0" strokeWidth={1.5} />
                <span className="text-[#FDFBF7] font-medium text-sm truncate">{c.label}</span>
                <span className="overline text-[9px] text-[#6C707A]">{formatDuration(c.duration_seconds)}</span>
                {!c.active && <span className="overline text-[9px] px-1.5 py-0.5 rounded bg-[#22262E] text-[#A1A4AB]">PAUSED</span>}
                {broadcastFlash?.id === c.id && (
                  <span className="overline text-[9px] px-1.5 py-0.5 rounded bg-[#C5A059] text-[#060709] inline-flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5" strokeWidth={2} /> Broadcast fired ({broadcastFlash.ttl}s window)
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => broadcast(c)}
                disabled={!c.active}
                title="Sound the shofar — broadcast to all listeners"
                data-testid={`shofar-broadcast-${c.id}`}
                className="overline text-[10px] px-3 py-1.5 rounded-md bg-[#C5A059] text-[#060709] hover:bg-[#DCC286] disabled:opacity-40 inline-flex items-center gap-1"
              >
                <Megaphone className="w-3 h-3" strokeWidth={2} /> Sound now
              </button>
              <button onClick={() => toggleActive(c)} className="p-1.5 rounded-md border border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7]" data-testid={`shofar-toggle-${c.id}`}>
                {c.active ? <ToggleRight className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} /> : <ToggleLeft className="w-4 h-4" strokeWidth={1.5} />}
              </button>
              <button onClick={() => remove(c)} className="p-1.5 rounded-md border border-red-400/30 text-red-300 hover:bg-red-500/10" data-testid={`shofar-delete-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
